import { zodResolver } from "@hookform/resolvers/zod";
import { CornerDownLeft } from "lucide-react";
import { type KeyboardEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { AccountDetails, CostCenters, TaxCode } from "@/apis/gql/types";
import { AccountDropdownInput } from "@/components/shared/form/account-dropdown-input";
import { TaxCodeDropdownInput } from "@/components/shared/form/tax-code-dropdown-input";
import { TextInput } from "@/components/shared/form/text-input";
import { AccountsDropdown } from "@/components/shared/select-inputs/accounts-dropdown";
import { RowsSelected } from "@/components/shared/tables/ActionBar";
import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useTenant } from "@/providers/session";
import { t } from "i18next";
import { InvoiceReviewActionBar as ActionBar } from "../../InvoiceReviewActionBar";

export interface BulkEditActionBarProps {
  table: any;
  accounts: AccountDetails[] | undefined;
  taxCodes: TaxCode[] | undefined;
  costCenters: CostCenters[] | undefined;
  handleCombinedAccountChange: (value: any) => void;
  handleCombinedTaxCodeChange: (value: any) => void;
  handleCombinedCostCentersChange: (value: any) => void;
  handleDeleteSelected: () => Promise<void>;
}
export interface CanvasBulkEditActionBarProps {
  table: any;
  accounts: AccountDetails[] | undefined;
  taxCodes: TaxCode[] | undefined;
  handleCombinedAccountChange: (value: any) => void;
  handleCombinedTaxCodeChange: (value: any) => void;
  handleCombinedNameChange: (value: any) => void;
  handleDeleteSelected: () => Promise<void>;
  handleDeselectAll: () => void;
  handleBulkSave: (value: CanvasBulkEditForm) => Promise<void>;
  selectedItems?: Set<any>;
  totalAmountBoxes: any[];
}

const initialState = {
  account: {
    value: "",
    label: "Sachkonto",
    alternativeLabels: [] as string[],
    subLabel: "",
  },
  taxCode: {
    value: "",
    label: "BU",
    alternativeLabels: [] as string[],
    subLabel: "",
  },
  costCenter: {
    value: "unknown",
    label: "Kostenstelle",
    alternativeLabels: [] as string[],
    subLabel: "",
  },
};

export function BulkEditActionBar({
  table,
  handleCombinedAccountChange,
  accounts,
  handleCombinedTaxCodeChange,
  taxCodes,
  costCenters,
  handleCombinedCostCentersChange,
  handleDeleteSelected,
}: BulkEditActionBarProps) {
  const [values, setValues] = useState(initialState);
  const { hasCostCenters } = useTenant();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to sync specifcally when the selected rows change
  useEffect(() => {
    setValues(initialState);
  }, [table?.getSelectedRowModel()?.rows.length]);

  const handleAccountChange = (value: any) => {
    handleCombinedAccountChange(value);

    // // Find the selected account from the options
    const selectedAccount = accounts?.find(
      (account) => account.accountId === value,
    );
    const currentSingleAccountOption = {
      label: selectedAccount?.accountName ?? "",
      alternativeLabels: selectedAccount?.accountNo
        ? [selectedAccount?.accountNo]
        : [],
      value: selectedAccount?.accountId ?? "",
      subLabel: selectedAccount?.accountNo || "Unbekannt",
    };

    setValues((prev) => ({
      ...prev,
      account: currentSingleAccountOption,
    }));
  };

  const handleTaxCodeChange = (value: any) => {
    handleCombinedTaxCodeChange(value);

    // // Find the selected account from the options
    const selectedTaxCode = taxCodes?.find(
      (taxCode) => taxCode.taxCodeId === value,
    );
    const currentTaxCodeOption = {
      label: selectedTaxCode?.name ?? "",
      alternativeLabels: selectedTaxCode?.datevBu
        ? [selectedTaxCode?.datevBu]
        : [],
      value: selectedTaxCode?.taxCodeId ?? "",
      subLabel: selectedTaxCode?.datevBu || "Unbekannt",
    };

    setValues((prev) => ({
      ...prev,
      taxCode: currentTaxCodeOption,
    }));
  };

  const handleCostCenterChange = (value: string) => {
    handleCombinedCostCentersChange(value);
    const selectedCostCenter = costCenters?.find(
      (costCenter) => costCenter.costCenterId === value,
    );
    const currentCostCenterOption = {
      label: selectedCostCenter?.name ?? "",
      alternativeLabels: selectedCostCenter?.identifier
        ? [selectedCostCenter?.identifier]
        : [],
      value: selectedCostCenter?.costCenterId ?? "",
      subLabel: selectedCostCenter?.identifier || "Unbekannt",
    };

    setValues((prev) => ({
      ...prev,
      costCenter: currentCostCenterOption,
    }));
  };

  const costCenterOptions =
    costCenters?.map(
      (costCenter, index): ComboOption => ({
        label: costCenter.name,
        subLabel: costCenter.identifier?.toString(),
        alternativeLabels: [],
        value: costCenter.costCenterId,
      }),
    ) ?? [];
  const options = [
    ...costCenterOptions,
    {
      label: t("cost_centers.no_cost_center"),
      subLabel: "0",
      alternativeLabels: [],
      value: "unknown",
    },
  ];

  return (
    <div className="flex flex-row items-center">
      <RowsSelected
        totalRowsLength={table?.getRowCount()}
        selectedRowsLength={table?.getSelectedRowModel()?.rows?.length}
        onDeselectAll={() => table.resetRowSelection(true)}
      />
      <AccountsDropdown
        currentOption={values.account}
        onChange={handleAccountChange}
        items={accounts ?? []}
        placeholder="Sachkonto"
        fullWidth={false}
        variant={"outline"}
        sideOffset={24}
      />
      <ComboBox
        currentOption={values.taxCode}
        onChange={handleTaxCodeChange}
        sideOffset={24}
        items={
          taxCodes?.map(
            (taxCode): ComboOption => ({
              label: taxCode.name,
              alternativeLabels: [taxCode.datevBu],
              value: taxCode.taxCodeId,
              subLabel: taxCode.datevBu,
            }),
          ) ?? []
        }
        fullWidth={false}
        placeholder="Steuerschlüssel"
        emptyLabel="Keine BU gefunden"
        variant={"outline"}
      />
      {hasCostCenters ? (
        <ComboBox
          currentOption={values.costCenter}
          onChange={handleCostCenterChange}
          sideOffset={24}
          items={options}
          fullWidth={false}
          placeholder={t("cost_centers.cost_center")}
          emptyLabel={t("cost_centers.no_cost_center")}
          variant={"outline"}
        />
      ) : null}
      <Button variant={"outline"} onClick={handleDeleteSelected}>
        Löschen
      </Button>
    </div>
  );
}

const canvasBulkEditFormSchema = z.object({
  name: z.string().min(3).max(255),
  accountId: z.string().min(1).max(255).optional(),
  taxCodeId: z.string().min(1).max(255).optional(),
});

type CanvasBulkEditForm = z.infer<typeof canvasBulkEditFormSchema>;

export function CanvasBulkEditActionBar({
  accounts,
  taxCodes,
  handleBulkSave,
  handleDeselectAll,
  selectedItems,
  totalAmountBoxes,
}: CanvasBulkEditActionBarProps) {
  const { t } = useTranslation();

  const form = useForm<CanvasBulkEditForm>({
    resolver: zodResolver(canvasBulkEditFormSchema),
    defaultValues: {
      name: "",
      accountId: undefined,
      taxCodeId: undefined,
    },
  });

  const handleSubmit = async ({
    name,
    accountId,
    taxCodeId,
  }: CanvasBulkEditForm) => {
    await handleBulkSave({ name, accountId, taxCodeId });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      form.handleSubmit(handleSubmit)();
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        onKeyDown={handleKeyDown}
      >
        <ActionBar stickyBottom={false} className="w-[90%]">
          <RowsSelected
            totalRowsLength={totalAmountBoxes?.length}
            selectedRowsLength={selectedItems?.size || 0}
            onDeselectAll={() => handleDeselectAll()}
          />
          <TextInput name="name" placeholder="Leistung" autoFocus />
          <AccountDropdownInput
            name="accountId"
            options={accounts ?? []}
            fullWidth={false}
            className="w-36"
            sideOffset={24}
          />
          <TaxCodeDropdownInput
            sideOffset={24}
            name="taxCodeId"
            options={taxCodes ?? []}
            fullWidth={false}
            className="w-36"
            placeholder="BU"
          />
          <Button
            type="submit"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            {t("save")}
            <CornerDownLeft className="ml-1" size={16} />
          </Button>
        </ActionBar>
      </form>
    </Form>
  );
}
